import { faMagnifyingGlass, faTrashCan } from "@fortawesome/pro-light-svg-icons";
import { InputAdornment, OutlinedInput, Stack } from "@mui/material";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { faPenLine, faRightToBracket } from "@fortawesome/pro-regular-svg-icons";

import {
	Avatar,
	Icon,
	LoadingOverlay,
	PermissionCheck,
	TableColumn,
	TableComponent,
} from "@/components";
import { PermissionRoles, UserStatus } from "@/enum";
import {
	useDeleteUserMutation,
	useGetUsersQuery,
	useLoginAsMutation,
	useResendInviteEmailMutation,
} from "@/pages/Private/redux/admin/admin.api";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { useSearch } from "@/utils";

import { authSelector, setAuth } from "@/pages/Public/redux/auth.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import DeleteModal from "@/components/Modals/DeleteModal";

import { EditUserModal } from "./EditUserModal";
import MultiRoleSelect from "./MultiRoleSelect";

export const UsersTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`users.${key}`);

	const { id } = useParams();
	const dispatch = useAppDispatch();
	const userTokenObj = useAppSelector(authSelector);
	const navigate = useNavigate();
	const [showEditModal, setShowEditModal] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [loginAsUser] = useLoginAsMutation();
	const {
		page,
		setPage,
		limit,
		setLimit,
		debouncedFilters,
		query,
		filters,
		setFilters,
		sortOrder,
		sortColumn,
		setSorting,
	} = useSearch();

	const { data: users, isLoading: isUsersLoading } = useGetUsersQuery({
		page: page,
		limit,
		searchValue: query,
		companyId: id ? +id : undefined,
		filters: debouncedFilters,
		sortBy: sortColumn,
		sortOrder: sortOrder,
	});
	const [deleteUser, { isLoading }] = useDeleteUserMutation();
	const [resendInviteEmail] = useResendInviteEmailMutation();

	const loginAs = async (user: User) => {
		localStorage.setItem("initialUser", JSON.stringify(userTokenObj));

		const newUser = await loginAsUser(user.id).unwrap();

		dispatch(setAuth(newUser));
		navigate("/");
	};

	const resendInvite = async (user: User) => {
		const userForResend = {
			email: user.email,
			roleId: user.role.id,
			firstName: user.firstName,
			lastName: user.lastName,
		};

		await resendInviteEmail(userForResend).unwrap();
	};

	const columns: TableColumn<User>[] = useMemo(
		() => [
			{
				sx: { "border-bottom": "0px", padding: "0px", width: "100%" },
				label: t("basics.name"),
				isSortable: true,
				fieldName: "name",
				minWidth: 350,
				format: (row) => (
					<Stack alignItems="center" direction="row" spacing={1}>
						<Avatar firstName={row.firstName} lastName={row.lastName} />
						<Stack direction="column" justifyContent="flex-start" ml={2}>
							{(row.firstName || row.lastName) && (
								<p className="text-[14px] font-medium leading-5 text-primary-blue-dark">
									{`${row.firstName} ${row.lastName}`}
								</p>
							)}

							<p className="text-[12px] font-normal leading-4 text-subtle">{row.email}</p>
						</Stack>
					</Stack>
				),
			},

			{
				sx: { "border-bottom": "0px", padding: "0px" },
				label: t("basics.status"),
				minWidth: 300,
				format: (row) => {
					const ownerState =
						row.status === UserStatus.ACTIVE
							? "#3DA318"
							: row.status === UserStatus.BLOCKED
							? "#DF2800"
							: row.status === UserStatus.INVITED
							? "#D9A400"
							: "#686761";

					return (
						<div className="flex flex-row items-center">
							<p
								className="flex gap-[6px] items-center justify-center text-[12px] font-medium capitalize leading-4 p-2 rounded-md"
								style={{ color: ownerState, backgroundColor: `${ownerState}1F` }}
							>
								<p
									className="rounded-full min-w-[6px] min-h-[6px] max-w-[6px] max-h-[6px]"
									style={{ backgroundColor: ownerState }}
								/>
								{row.status.toLowerCase()}
							</p>
							{row.status === UserStatus.INVITED && (
								<button
									className="text-[14px] font-medium ml-1 text-primary-blue-dark hover:text-primary-blue leading-4"
									onClick={() => resendInvite(row)}
								>
									{t("basics.resend-invite")}
								</button>
							)}
						</div>
					);
				},
			},
			{
				sx: { "border-bottom": "0px", padding: "0px" },
				label: t("basics.role"),
				minWidth: 200,
				format: (row) => {
					return (
						<p className="text-[14px] font-medium leading-5 text-primary-blue-dark">
							{row.role.name}
						</p>
					);
				},
			},
			{
				sx: { "border-bottom": "0px", padding: "0px" },
				align: "center",
				label: t("basics.actions"),
				minWidth: 200,
				format: (row) => (
					<div className="flex gap-2 !w-fit !ml-auto items-center">
						{id ? (
							<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
								<button
									className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-border"
									onClick={() => loginAs(row)}
								>
									<Icon icon={faRightToBracket} size="lg" />
								</button>
							</PermissionCheck>
						) : (
							<></>
						)}
						<button
							className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-border"
							onClick={() => handleEditUser(row)}
						>
							<Icon icon={faPenLine} size="lg" />
						</button>
						<button
							className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-border"
							onClick={() => handleDeleteModal(row)}
						>
							<Icon icon={faTrashCan} size="lg" />
						</button>
					</div>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[id, t]
	);

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setLimit(Number(event.target.value));
		setPage(0);
	};

	const handleEditUser = (row: User) => {
		setShowEditModal(true);
		setSelectedUser(row);
	};

	const handleDeleteModal = (row: User) => {
		setShowDeleteModal(true);
		setSelectedUser(row);
	};

	const handleDeleteUser = async () => {
		try {
			await deleteUser(selectedUser?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleCloseEditModal = () => {
		setShowEditModal(false);
		setSelectedUser(null);
	};

	return !users && !isUsersLoading ? (
		<LoadingOverlay />
	) : (
		<>
			<div className="flex w-full gap-2">
				<MultiRoleSelect
					selectedRoles={Array.isArray(filters["role"]) ? filters["role"] : [filters["role"]]}
					onChange={(roles) => setFilters({ role: roles })}
				/>
				<OutlinedInput
					fullWidth
					inputProps={{
						style: {
							fontSize: "14px",
							padding: "0px",
							fontFamily: "Urbanist",
							lineHeight: "20px",
						},
					}}
					placeholder={t("basics.search", { something: ts("search") })}
					startAdornment={
						<InputAdornment position="start">
							<Icon icon={faMagnifyingGlass} />
						</InputAdornment>
					}
					sx={{
						paddingX: "12px",
						paddingY: "11px",
						borderRadius: "12px",
						"& .MuiInputBase-input": {
							color: "#11110F",
							fontWeight: "500",
						},

						"& .MuiInputBase-input::placeholder": {
							color: "#686761",
							fontWeight: "400",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: "#FF5A24 !important",
							borderWidth: "1px !important",
							boxShadow: "none",
						},
					}}
					value={filters["name"] ?? ""}
					onChange={(e) => setFilters({ name: e.target.value })}
				/>
			</div>
			<TableComponent
				bodySx={{
					overflowY: "auto",
					borderRadius: "16px",
					backgroundColor: "#F4F3F1",
				}}
				columns={columns}
				data={users?.data || []}
				handleChangeLimit={handleChangeRowsPerPage}
				handleChangePage={(_: unknown, page: number) => setPage(page + 1)}
				headerSx={{
					display: "block",
					paddingX: "8px",
					backgroundColor: "#F4F3F1",
				}}
				itemCount={users?.meta?.itemCount ?? 0}
				limit={limit}
				page={page}
				rowSx={{
					display: "block",
					padding: "8px",
					borderRadius: "12px",
					boxShadow: "0px 1px 2px 0px #11110F1F",
					backgroundColor: "#FFFFFF",
					marginBottom: "4px",
				}}
				setSortDirection={setSorting}
				sortColumn={sortColumn}
				sortDirection={sortOrder}
				tableSx={{
					paddingX: "4px",
					paddingTop: "0px",
					borderRadius: "16px",
					backgroundColor: "#F4F3F1",
					border: "1px solid #DCDAD6",
					width: "100%",
				}}
			/>

			{showEditModal && (
				<EditUserModal
					companyId={id ? +id : undefined}
					handleClose={handleCloseEditModal}
					isVisible={showEditModal}
					user={selectedUser}
				/>
			)}

			{showDeleteModal && (
				<DeleteModal
					description={t("modals.deleteUser.description", {
						firstName: selectedUser?.firstName,
						lastName: selectedUser?.lastName,
					})}
					handleClose={() => setShowDeleteModal(false)}
					handleDelete={handleDeleteUser}
					isLoading={isLoading}
					isShowing={showDeleteModal}
					title={t("modals.deleteUser.title")}
				/>
			)}
		</>
	);
};
